import React, { useState, useEffect } from 'react';
import { sportsApi } from '../services/sportsApi';
import { Club, League, Sport, Country } from '../types/sports';
import ClubForm from '../components/clubs/ClubForm';
import ConfirmDialog from '../components/common/ConfirmDialog';
import Toast from '../components/common/Toast';
import { useToast } from '../hooks/useToast';
import { Plus, Pencil, Trash2, CheckCircle, XCircle, LayoutGrid, List } from 'lucide-react';
import { usePreferredViewMode, STANDARD_VIEW_MODE_MAP } from '../hooks/usePreferredViewMode';
import '../styles/PageLayout.css';

/**
 * Clubs management page with full CRUD functionality
 */
const Clubs: React.FC = () => {
  const [clubs, setClubs] = useState<Club[]>([]);
  const [leagues, setLeagues] = useState<League[]>([]);
  const [sports, setSports] = useState<Sport[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = usePreferredViewMode<'list' | 'cards'>('cards', STANDARD_VIEW_MODE_MAP);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSport, setSelectedSport] = useState<number | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<number | null>(null);
  const [selectedLeague, setSelectedLeague] = useState<number | null>(null);
  
  // CRUD states
  const [showForm, setShowForm] = useState(false);
  const [editingClub, setEditingClub] = useState<Club | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [clubToDelete, setClubToDelete] = useState<Club | null>(null);
  // Toast
  const { toast, showSuccess, showError } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [clubsData, leaguesData, sportsData, countriesData] = await Promise.all([
        sportsApi.getClubs(),
        sportsApi.getLeagues(),
        sportsApi.getSports(),
        sportsApi.getCountries()
      ]);
      setClubs(clubsData);
      setLeagues(leaguesData);
      setSports(sportsData);
      setCountries(countriesData);
    } catch (err) {
      setError('Fehler beim Laden der Vereine');
      console.error('Error loading clubs:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadClubs = async () => {
    try {
      const clubsData = await sportsApi.getClubs();
      setClubs(clubsData);
    } catch (err) {
      console.error('Error loading clubs:', err);
      showError('Fehler beim Laden der Vereine');
    }
  };

  // CRUD Functions
  const handleCreateClub = () => {
    setEditingClub(null);
    setShowForm(true);
  };

  const handleEditClub = (club: Club) => {
    setEditingClub(club);
    setShowForm(true);
  };

  const handleDeleteClub = async (club: Club) => {
    try {
      const canDeleteResponse = await sportsApi.canDeleteClub(club.id);
      if (!canDeleteResponse.canDelete) {
        showError('Dieser Verein kann nicht gelöscht werden, da er von anderen Datensätzen referenziert wird.');
        return;
      }
      setClubToDelete(club);
      setShowDeleteDialog(true);
    } catch (error) {
      console.error('Error checking if club can be deleted:', error);
      showError('Fehler beim Prüfen der Löschberechtigung.');
    }
  };

  const handleSaveClub = async (data: {
    name: string;
    shortName?: string;
    description?: string;
    city?: string;
    stadium?: string;
    stadiumAddress?: string;
    isActive?: boolean;
    sportId?: number;
    countryId?: number;
    leagueId?: number;
  }) => {
    try {
      setFormLoading(true);
      if (editingClub) {
        await sportsApi.updateClub(editingClub.id, data);
        showSuccess('Verein erfolgreich aktualisiert');
      } else {
        // Create new club
        if (!data.sportId || !data.countryId) {
          showError('Sport und Land sind Pflichtfelder');
          return;
        }
        await sportsApi.createClub({
          name: data.name,
          shortName: data.shortName,
          description: data.description,
          city: data.city,
          stadium: data.stadium,
          stadiumAddress: data.stadiumAddress,
          sportId: data.sportId,
          countryId: data.countryId,
          leagueId: data.leagueId
        });
        showSuccess('Verein erfolgreich erstellt');
      }
      setShowForm(false);
      setEditingClub(null);
      await loadClubs();
    } catch (err: any) {
      const errorMessage = err.message || (editingClub ? 'Fehler beim Aktualisieren des Vereins' : 'Fehler beim Erstellen des Vereins');
      showError(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!clubToDelete) return;

    try {
      await sportsApi.deleteClub(clubToDelete.id);
      showSuccess('Verein erfolgreich gelöscht');
      setShowDeleteDialog(false);
      setClubToDelete(null);
      await loadClubs();
    } catch (err: any) {
      showError(err.message || 'Fehler beim Löschen des Vereins');
    }
  };

  const filteredClubs = clubs.filter(club => {
    const matchesSearch = club.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (club.shortName && club.shortName.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesSport = !selectedSport || club.sportId === selectedSport;
    const matchesCountry = !selectedCountry || club.countryId === selectedCountry;
    const matchesLeague = !selectedLeague || club.leagueId === selectedLeague;
    
    return matchesSearch && matchesSport && matchesCountry && matchesLeague;
  });

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-loading">
          <div className="page-spinner"></div>
          <p>Vereine werden geladen...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="page-error">
          <h2>Fehler beim Laden der Vereine</h2>
          <p>{error}</p>
          <button onClick={loadData}>Erneut versuchen</button>
        </div>
      </div>
    );
  }

  if (showForm) {
    return (
      <div className="page-container">
        <ClubForm
          club={editingClub}
          sports={sports}
          countries={countries}
          leagues={leagues}
          onSave={handleSaveClub}
          onCancel={() => {
            setShowForm(false);
            setEditingClub(null);
          }}
          loading={formLoading}
        />
        {toast && <Toast {...toast} onClose={() => {}} />}
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Controls – two-row Menüband */}
      <div className="page-controls">
        {/* Row 1: Buttons */}
        <div className="page-controls-row">
          <button className="page-btn page-btn-create" onClick={handleCreateClub}>
            <Plus size={14} /> Neu
          </button>
          <div className="page-controls-spacer" />
          <div className="page-view-toggle">
            <button className={`page-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`} onClick={() => setViewMode('cards')} title="Kacheln">
              <LayoutGrid size={14} />
            </button>
            <button className={`page-toggle-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} title="Liste">
              <List size={14} />
            </button>
          </div>
        </div>
        {/* Row 2: Search + Filters + record count */}
        <div className="page-controls-row">
          <input
            type="text"
            className="page-search-input"
            placeholder="Vereine suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select
            className="page-filter-select"
            value={selectedSport || ''}
            onChange={(e) => setSelectedSport(e.target.value ? parseInt(e.target.value) : null)}
          >
            <option value="">Alle Sportarten</option>
            {sports.map(sport => (
              <option key={sport.id} value={sport.id}>
                {sport.icon ? `${sport.icon} ` : ''}{sport.name}
              </option>
            ))}
          </select>
          <select
            className="page-filter-select"
            value={selectedCountry || ''}
            onChange={(e) => setSelectedCountry(e.target.value ? parseInt(e.target.value) : null)}
          >
            <option value="">Alle Länder</option>
            {countries.map(country => (
              <option key={country.id} value={country.id}>
                {country.flag ? `${country.flag} ` : ''}{country.name}
              </option>
            ))}
          </select>
          <select
            className="page-filter-select"
            value={selectedLeague || ''}
            onChange={(e) => setSelectedLeague(e.target.value ? parseInt(e.target.value) : null)}
          >
            <option value="">Alle Ligen</option>
            {leagues
              .filter(league => !selectedSport || league.sportId === selectedSport)
              .filter(league => !selectedCountry || league.countryId === selectedCountry)
              .map(league => (
                <option key={league.id} value={league.id}>
                  {league.name} (Level {league.level})
                </option>
              ))}
          </select>
          <div className="page-controls-spacer" />
          <span className="page-record-count">{filteredClubs.length} / {clubs.length} Vereine</span>
        </div>
      </div>

      {/* Clubs Content */}
      {viewMode === 'cards' ? (
        <div className="page-cards-grid">
          {filteredClubs.map(club => (
            <div key={club.id} className="page-card">
              <div className="page-card-header">
                <h3 className="page-card-title">{club.name}</h3>
              </div>
              <div className="page-card-content">
                {club.shortName && <span><strong>Kürzel:</strong> {club.shortName}</span>}
                {club.description && <span><strong>Beschreibung:</strong> {club.description}</span>}
                <span><strong>Sport:</strong> {club.sport?.name || 'Unbekannt'}</span>
                <span><strong>Land:</strong> {club.country?.name || 'Unbekannt'}</span>
                {club.league && <span><strong>Liga:</strong> {club.league.name}</span>}
                <div className="page-card-row">
                  <span className={`page-badge ${club.isActive ? 'page-badge-active' : 'page-badge-inactive'}`}>
                    {club.isActive ? <><CheckCircle size={12} /> Aktiv</> : <><XCircle size={12} /> Inaktiv</>}
                  </span>
                  <span className="page-card-footer">ID: {club.id}</span>
                </div>
              </div>
              <div className="page-card-actions">
                <button className="page-btn-icon edit" onClick={() => handleEditClub(club)} title="Bearbeiten">
                  <Pencil size={14} />
                </button>
                <button className="page-btn-icon delete" onClick={() => handleDeleteClub(club)} title="Löschen">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="page-data-panel">
          <table className="page-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Kürzel</th>
                <th>Sport</th>
                <th>Land</th>
                <th>Liga</th>
                <th>Status</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filteredClubs.map(club => (
                <tr key={club.id}>
                  <td>{club.name}</td>
                  <td>{club.shortName || '-'}</td>
                  <td>{club.sport?.name || 'Unbekannt'}</td>
                  <td>{club.country?.name || 'Unbekannt'}</td>
                  <td>{club.league?.name || '-'}</td>
                  <td>
                    <span className={`page-badge ${club.isActive ? 'page-badge-active' : 'page-badge-inactive'}`}>
                      {club.isActive ? <><CheckCircle size={12} /> Aktiv</> : <><XCircle size={12} /> Inaktiv</>}
                    </span>
                  </td>
                  <td>
                    <div className="page-table-actions">
                      <button className="page-btn-icon edit" onClick={() => handleEditClub(club)} title="Bearbeiten">
                        <Pencil size={14} />
                      </button>
                      <button className="page-btn-icon delete" onClick={() => handleDeleteClub(club)} title="Löschen">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Dialogs */}
      {showDeleteDialog && clubToDelete && (
        <ConfirmDialog
          isOpen={showDeleteDialog}
          title="Verein löschen"
          message={`Möchten Sie den Verein "${clubToDelete.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
          onConfirm={confirmDelete}
          onCancel={() => {
            setShowDeleteDialog(false);
            setClubToDelete(null);
          }}
          type="danger"
        />
      )}

      {toast && <Toast {...toast} onClose={() => {}} />}
    </div>
  );
};

export default Clubs;
